import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';

const normalizeAllowanceValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1' || normalized === 'allowed' || normalized === 'yes') return true;
    if (normalized === 'false' || normalized === '0' || normalized === 'not allowed' || normalized === 'no') return false;
  }
  return null;
};

const emptyMember = {
  id: null,
  name: '',
  surname: '',
  memberId: '',
  locationId: null,
  groupId: null,
  type: '',
  allowance: '',
  initDate: '',
  endDate: '',
  extraFields: '',
  comments: '',
  temporal: false,
  email: '',
  purpose: '',
  mobile: '',
};

const toFormState = (member, defaultGroupId) => ({
  ...emptyMember,
  ...member,
  groupId: member && member.groupId !== null && member.groupId !== undefined
    ? Number(member.groupId)
    : defaultGroupId,
  locationId: member && member.locationId !== null && member.locationId !== undefined
    ? Number(member.locationId)
    : null,
  temporal: Boolean(member && member.temporal),
});

const MemberFormModal = ({
  visible,
  member,
  onHide,
  onSave,
  saving,
  customerOptions = [],
  locationOptions = [],
  defaultGroupId = null,
  lockGroupId = false,
  allowGroupEditOnExisting = false,
  onGroupChange,
  minimalCreateMode = false,
  supportRestrictedEdit = false,
}) => {
  const [form, setForm] = useState(() => toFormState(member, defaultGroupId));
  const isEditing = Boolean(member && member.id);
  const showMinimalCreateFields = minimalCreateMode && !isEditing;
  const showSupportRestrictedEdit = supportRestrictedEdit && isEditing;
  const allowanceValue = normalizeAllowanceValue(form.allowance);

  const customerName = useMemo(() => {
    if (member && member.customerName) return String(member.customerName);

    const match = (customerOptions || []).find((option) => Number(option.value) === Number(form.groupId));
    return match ? String(match.label || '') : '';
  }, [member, customerOptions, form.groupId]);

  useEffect(() => {
    setForm(toFormState(member, defaultGroupId));
  }, [member, defaultGroupId, visible]);

  const canEditGroup = useMemo(() => {
    if (lockGroupId) return false;
    if (member && member.id) return allowGroupEditOnExisting;
    return true;
  }, [lockGroupId, member, allowGroupEditOnExisting]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleGroupChange = (value) => {
    updateField('groupId', value);
    updateField('locationId', null);
    if (onGroupChange) onGroupChange(value);
  };

  const footer = (
    <div>
      <Button label="Cancel" className="p-button-text" onClick={onHide} />
      <Button label="Save" icon="pi pi-check" onClick={() => onSave(form)} loading={saving} />
    </div>
  );

  return (
    <Dialog
      header={isEditing ? 'Edit Member' : 'Create Member'}
      visible={visible}
      onHide={onHide}
      style={{ width: '760px' }}
      modal
      footer={footer}
    >
      <div className="p-fluid formgrid grid">
        <div className="field col-12 md:col-6 mb-3">
          <label htmlFor="member-name">Name</label>
          <InputText id="member-name" value={form.name} onChange={(e) => updateField('name', e.target.value)} />
        </div>
        <div className="field col-12 md:col-6 mb-3">
          <label htmlFor="member-surname">Surname</label>
          <InputText id="member-surname" value={form.surname} onChange={(e) => updateField('surname', e.target.value)} />
        </div>
        {showSupportRestrictedEdit ? (
          <div className="field col-12 md:col-6 mb-3">
            <label htmlFor="member-allowance" className="block mb-2">Allowance</label>
            <div className="flex align-items-center gap-3">
              <InputSwitch
                id="member-allowance"
                checked={allowanceValue === true}
                onChange={(e) => updateField('allowance', Boolean(e.value))}
              />
              <span>{allowanceValue === true ? 'Allowed' : 'Not allowed'}</span>
            </div>
          </div>
        ) : null}
        {showSupportRestrictedEdit ? null : (
          <>
        {showMinimalCreateFields ? null : (
          <>
        <div className="field col-12 md:col-6 mb-3">
          <label htmlFor="member-memberid">Member ID</label>
          <InputText id="member-memberid" value={form.memberId} onChange={(e) => updateField('memberId', e.target.value)} />
        </div>
        <div className="field col-12 md:col-6 mb-3">
          <label htmlFor="member-group">Group</label>
          <Dropdown
            id="member-group"
            value={form.groupId}
            options={customerOptions}
            onChange={(e) => handleGroupChange(e.value)}
            placeholder="Select customer"
            filter
            disabled={!canEditGroup}
          />
        </div>
        <div className="field col-12 md:col-6 mb-3">
          <label htmlFor="member-customername">Customer</label>
          <InputText id="member-customername" value={customerName} readOnly disabled />
        </div>
        <div className="field col-12 md:col-6 mb-3">
          <label htmlFor="member-location">Location</label>
          <Dropdown
            id="member-location"
            value={form.locationId}
            options={locationOptions}
            onChange={(e) => updateField('locationId', e.value)}
            placeholder="Select location"
            filter
          />
        </div>
          </>
        )}
        {showMinimalCreateFields ? (
          <div className="field col-12 md:col-6 mb-3">
            <label htmlFor="member-location">Location</label>
            <Dropdown
              id="member-location"
              value={form.locationId}
              options={locationOptions}
              onChange={(e) => updateField('locationId', e.value)}
              placeholder="Select location"
              filter
            />
          </div>
        ) : null}
        {showMinimalCreateFields ? null : (
          <>
        <div className="field col-12 md:col-6 mb-3">
          <label htmlFor="member-type">Type</label>
          <InputText id="member-type" value={form.type || ''} onChange={(e) => updateField('type', e.target.value)} />
        </div>
        <div className="field col-12 md:col-6 mb-3">
          <label htmlFor="member-allowance">Allowance</label>
          <InputText id="member-allowance" value={form.allowance !== null && form.allowance !== undefined ? form.allowance : ''} onChange={(e) => updateField('allowance', e.target.value)} />
        </div>
        <div className="field col-12 md:col-6 mb-3">
          <label htmlFor="member-initdate">Init Date</label>
          <InputText id="member-initdate" type="date" value={form.initDate || ''} onChange={(e) => updateField('initDate', e.target.value)} />
        </div>
        <div className="field col-12 md:col-6 mb-3">
          <label htmlFor="member-enddate">End Date</label>
          <InputText id="member-enddate" type="date" value={form.endDate || ''} onChange={(e) => updateField('endDate', e.target.value)} />
        </div>
        <div className="field col-12 md:col-6 mb-3">
          <label htmlFor="member-email">Email</label>
          <InputText id="member-email" value={form.email || ''} onChange={(e) => updateField('email', e.target.value)} />
        </div>
        <div className="field col-12 md:col-6 mb-3">
          <label htmlFor="member-mobile">Mobile</label>
          <InputText id="member-mobile" value={form.mobile || ''} onChange={(e) => updateField('mobile', e.target.value)} />
        </div>
        <div className="field col-12 md:col-6 mb-3">
          <label htmlFor="member-purpose">Purpose</label>
          <InputText id="member-purpose" value={form.purpose || ''} onChange={(e) => updateField('purpose', e.target.value)} />
        </div>
        <div className="field col-12 md:col-6 mb-3">
          <label htmlFor="member-extrafields">Extra Fields</label>
          <InputText id="member-extrafields" value={form.extraFields || ''} onChange={(e) => updateField('extraFields', e.target.value)} />
        </div>
        <div className="field col-12 mb-3">
          <label htmlFor="member-comments">Comments</label>
          <InputText id="member-comments" value={form.comments || ''} onChange={(e) => updateField('comments', e.target.value)} />
        </div>
        <div className="field col-12 md:col-4 mb-3">
          <label htmlFor="member-temporal" className="block mb-2">Temporal</label>
          <InputSwitch id="member-temporal" checked={Boolean(form.temporal)} onChange={(e) => updateField('temporal', e.value)} />
        </div>
          </>
        )}
          </>
        )}
      </div>
    </Dialog>
  );
};

export default MemberFormModal;
